import React, { Fragment, useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import _ from 'lodash';
import ReactGA from 'react-ga'
import { boot } from 'bizos-commons-loan-simulator';
import { Button, Checkbox, Switch } from 'components/commons';
import ButtonGroup from 'components/button-group';
import useDebounce from 'components/debounce';
import Dropdown from 'components/dropdown';
import Loading from 'components/loading';
import Slider from 'components/slider';
import LoanAmountInput from 'components/commons/loan-amount-input';
import PaymentSummary from 'components/payment-summary';
import Title from 'components/title';
import { CONFIG, productConfig, PRODUCTS_WITH_RECEIVABLES } from 'constants/constants';
import { ESTIMULO_2020, ESTIMULO_2020_BR, ESTIMULO_2020_RS, SEBRAE, BNDES, CAPITAL_GIRO } from 'constants/product-constants';
import {
    Proposal,
    useUnsetCouponCodeMutation,
    useApplyForCouponMutation,
    useAvailableCouponByCodeLazyQuery,
    useSetAcceptedProposalMutation,
    useGetLoanQuery,
    useAntiFraudLazyQuery,
    ProposalInput
} from 'generated/graphql';
import { useNavigation } from 'hooks/index';
import CouponInput from 'components/commons/coupon-input/coupon-input';
import { comission, recalculateSimulationResult } from 'utils/simulation-utils';
import {
    formatMoney,
    validateObject,
    getQueryParamsAsJson,
    formatMoneyWithoutDecimals,
    formatCnpj,
    getLoanApplicationParams
} from 'utils/utils';
import { IDiscountCalculated, ISimulationResult } from 'interfaces/interfaces';
import {
    calculateDiscounts,
    hasDiscountsToApply,
    hasComissionDiscount,
    hasInterestRateDiscount
} from 'utils/discount-utils';
import useOptimizeConfig from 'components/optimize/optimize-hook';
import MinimumInterestResponseCard from 'components/minimum-interest-response-card';
import { useProfile, getUtmSource, getUtmMedium } from 'providers/profile.provider';
import { useProduct } from 'providers/product.provider';
import useNeuroId from 'hooks/neuro-id';
import { registerTagmanagerEvent } from 'configs/trackers';
import { APP_PROFILE } from 'env';
import interestIcon from 'assets/img/icon-juros@2x.png';
import interestIcon2 from 'assets/img/icon-juros@3x.png';
import dropdownIcon from 'assets/svg/dropdown.svg';
import './../assets/scss/loan-configuration.scss'
import Info from 'assets/svg/info';
import AboutInsurance from 'components/commons/about-insurance/about-insurance';
import TermsDialog from 'components/commons/terms-dialog';
import InsuranceTermsText from 'components/insurance-terms-text';
import { PaymentInterval } from 'bizcap-commons-web/dist/loan/simulator/simulator';
import { isAnyEstimuloProduct } from 'utils/product-utils';
import { useAcceptedProposal } from 'providers/accepted-proposal.provider';

window.onload = async () => {
    try {
        await boot();
    } catch (error) {
        console.error(error);
    }
};

const LoanConfiguration: React.FC = () => {
    const { setUserId, setCheckpoint } = useNeuroId();
    const {
        nextScreen,
        goToFeedback,
        goToPreApprovedHome,
        goToAntiFraudCheckout,
        goToShareAntiFraudUrl,
        goToLoanInformation
    } = useNavigation();
    const [livenessLoading, setLivenessLoading] = useState(false);
    const [acceptedProposal, setAcceptedProposal] = useState<Proposal>();
    const [insuranceEnabled, setInsuranceEnabled] = useState(true);
    const [applyForCoupon] = useApplyForCouponMutation();
    const [unsetCouponCode] = useUnsetCouponCodeMutation();
    const [availableCouponByCodeQuery] = useAvailableCouponByCodeLazyQuery();
    const { componentText, showLoanConfigurationWithBorderedInput } = useOptimizeConfig();
    const { setAcceptedProposal: setAcceptedProposalVariable } = useAcceptedProposal();

    const {
        loanApplicationUtmSource,
        setLoanApplicationUtmSource,
        loanApplicationUtmMedium,
        setLoanApplicationUtmMedium
    } = useProfile();
    const loanApplicationId = getLoanApplicationParams();

    const [antiFraudQuery] = useAntiFraudLazyQuery({
        fetchPolicy: "network-only"
    });

    const { data, loading } = useGetLoanQuery({
        variables: {
            id: loanApplicationId
        },
        fetchPolicy: "network-only"
    });

    const {
        isPreApproved,
        productBizId,
        maxNumberOfInstallments,
        minNumberOfInstallments,
        setProductBizId,
        setMinNumberOfInstallments,
        setMaxNumberOfInstallments
    } = useProduct();

    const [setAcceptedProposalMutation] = useSetAcceptedProposalMutation();

    const { LOAN_AMOUNT, TENURE, PAYMENT_DAY } = productConfig(productBizId, maxNumberOfInstallments, minNumberOfInstallments);

    let paymentDayOptions = PAYMENT_DAY.AVAILABLE_OPTIONS();

    const computePossibleInstallments = (maxTenure, interestRate) => {
        const possibleDays = [];

        for (let i = TENURE.MIN(); i <= maxTenure; i++) {
            if (productBizId == BNDES && interestRate === 3.79 && i < 12) {
                continue;
            }
            possibleDays.push({ label: `${i} parcelas`, value: i });
        }

        return possibleDays;
    }

    useEffect(() => {
        const acceptedProposal: Proposal = {
            amount: data?.loanApplication.lastProposal.amount,
            date: data?.loanApplication.lastProposal.date,
            fixedComission: data?.loanApplication.lastProposal.fixedComission,
            interestRate: data?.loanApplication.lastProposal.interestRate,
            tenure: data?.loanApplication.lastProposal.tenure,
            downPayments: data?.loanApplication.lastProposal.downPayments,
            guaranteeConcessionFee: data?.loanApplication.lastProposal.guaranteeConcessionFee ?? 0,
        };

        setAcceptedProposal(acceptedProposal);
    }, [data?.loanApplication.lastProposal])

    const [discountCalculated, setDiscountCalculated] = useState<IDiscountCalculated>({});
    const [discounts, setDiscounts] = useState([]);
    const [invalidFields, setInvalidFields] = useState([]);
    const [form, setForm] = useState(acceptedProposal);
    const [showValidation, setShowValidation] = useState(false);
    const [amount, setAmount] = useState<any>(0);
    const [isDesktop, setDesktop] = useState(false);
    const debouncedAmount = useDebounce(amount, 200);
    const [state, setState] = useState({ submitting: false });
    const [couponButtonClasses, setCouponButtonClasses] = useState("primary-text bg-white border-0 insert-coupon-button pl-0 coupon-hitbox");
    const [showCouponInput, setShowCouponInput] = useState(false);
    const [couponError, setCouponError] = useState(false);
    const [showCouponResponseCard, setShowCouponResponseCard] = useState(false);
    const [couponCode, setCouponCode] = useState("");
    const [lastInputValue, setLastInputValue] = useState(null)
    const [showMinimumInterestCard, setShowMinimumInterestCard] = useState(false);
    const [minimumInterestError, setMinimumInterestError] = useState(false);
    const [sendProposalEvent, setSendProposalEvent] = useState(true);
    const [eligibleInsurance, setEligibleInsurance] = useState(true);
    const [insuranceOptIn, setInsuranceOptIn] = useState(false);
    const [showInsuranceAbout, setShowInsuranceAbout] = useState(false);
    const [simulation, setSimulation] = useState({ cet: {} } as ISimulationResult);

    useEffect(() => {
        if (sendProposalEvent && form && form.amount && form.amount > 0) {
            const commisionValue = comission({
                ...form,
                amount: form.amount,
                tenure: form.tenure,
                hasRenegotiation: data?.loanApplication.hasRenegotiation,
                productBizId,
                paymentInterval: form.paymentInterval ?? PaymentInterval.Monthly,
            })
                .toFixed(2);

            if (commisionValue) {
                registerProposalSentEvent(commisionValue);
                setSendProposalEvent(false);
            }
        }
    }, [form])

    const formIsValid = (): boolean => {
        if (invalidFields && invalidFields.length > 0 || (insuranceEnabled && !insuranceOptIn)) {
            return false;
        }

        return true;
    }

    useEffect(() => {
        if (data && discounts && debouncedAmount) {
            const acceptedProposal = {
                ...form,
                fixedComission: hasComissionDiscount(
                    discounts,
                    data?.loanApplication
                )
                    ? true
                    : form.fixedComission,
                interestRate: discountCalculated.calculatedInterestRate
                    ? discountCalculated.calculatedInterestRate
                    : form.interestRate,
                paymentDay: discountCalculated.calculatedPaymentDay
                    ? discountCalculated.calculatedPaymentDay
                    : form.paymentDay,
            };

            if ((acceptedProposal.interestRate === null || acceptedProposal.interestRate === undefined) || !acceptedProposal.tenure) {
                return null;
            }

            setSimulation(recalculateSimulationResult(
                {
                    ...data?.loanApplication,
                    acceptedProposal
                },
                discounts.filter(
                    (d) => d.__typename === 'ComissionDiscount'
                ),
                insuranceEnabled,
                productBizId
            ));
        }
    }, [debouncedAmount, data, discounts, form, eligibleInsurance, insuranceEnabled])

    const paymentSummary = useMemo(() => {
        if (!form) {
            return <Loading />;
        }

        return <PaymentSummary
            proposal={form}
            simulation={simulation}
            insurance={insuranceEnabled}
        >
            {
                !isDesktop && (
                    <ButtonGroup className="buttons-group-mobile">
                        {![ESTIMULO_2020, ESTIMULO_2020_BR, ESTIMULO_2020_RS, SEBRAE].includes(
                            loanApplicationUtmSource
                        ) &&
                            !isPreApproved && (
                                <Button
                                    label="ver depois"
                                    className="grid-button-backward"
                                    variant="secondary"
                                    onClick={goToFeedback}
                                />
                            )}
                        {isPreApproved && (
                            <Button
                                label="voltar"
                                className="grid-button-backward"
                                variant="secondary"
                                onClick={goToPreApprovedHome}
                            />
                        )}
                        <Button
                            label="continuar"
                            disabled={!formIsValid()}
                            className="grid-button-forward"
                            loading={livenessLoading}
                            onClick={() => onClickSubmit()}
                        />
                    </ButtonGroup>
                )}
        </PaymentSummary>

    }, [simulation]);

    const registerProposalSentEvent = async (commisionValue: string): Promise<void> => {
        try {
            registerTagmanagerEvent('proposal_sent', {
                loanApplicationId: data.loanApplication.id,
                firstProposalAmount: data.loanApplication.lastProposal.amount,
                commission: commisionValue
            });
        } catch (error) {
            console.error(error);
        }
    };

    const initializeForm = () => {
        if (acceptedProposal) {
            if (paymentDayOptions.length === 1) {
                acceptedProposal.paymentDay = paymentDayOptions[0].value;
            } else if (!paymentDayOptions.find(d => d.value === acceptedProposal.paymentDay)) {
                const defaultPaymentDay = paymentDayOptions.find(d => moment(d.value).date() === PAYMENT_DAY.DEFAULT);
                acceptedProposal.paymentDay = defaultPaymentDay
                    ? defaultPaymentDay.value
                    : paymentDayOptions[0].value;
            }

            setForm(acceptedProposal);

            setAmount(acceptedProposal.amount);
            setInvalidFields(validateProposal(acceptedProposal));
            if (CONFIG.COUPON.CAN_APPLY() && data?.loanApplication.couponCode) {
                availableCouponByCodeQuery({
                    variables: {
                        code: data?.loanApplication.couponCode
                    }
                }).then(({ data: { availableCouponByCode } }) => (
                    availableCouponByCode &&
                    loadDiscounts(data?.loanApplication.couponCode, availableCouponByCode)
                )
                )
            }
        }
    }

    const validateProposal = (proposal: Proposal): any[] => {
        const validation = [];

        if (proposal.amount > data?.loanApplication.lastProposal.amount || proposal.amount < LOAN_AMOUNT.MIN()) {
            validation.push('amount');
        }

        if ((proposal.interestRate == null || proposal.interestRate == undefined) && getUtmSource() !== SEBRAE) {
            validation.push('interestRate');
        }

        return validateObject(proposal).concat(validation);
    }

    const validateForSubmit = () => {
        if (state.submitting) {
            setInvalidFields(validateProposal(form));
            setShowValidation(true);
        }
    }

    useEffect(() => {
        if (!loanApplicationId) {
            return;
        }
        setUserId(loanApplicationId);
    }, [loanApplicationId]);

    const onClickSubmit = () => {
        setState({ ...state, submitting: true })
    }

    const submit = () => {
        if (state.submitting && !invalidFields.length) {

            const proposal: ProposalInput = {
                amount: form.amount,
                interestRate: form.interestRate,
                tenure: form.tenure,
                paymentDay: form.paymentDay,
                date: form.date,
                fixedComission: form.fixedComission,
                commission: parseFloat(form.fixedComission ? form.commission : comission({
                    ...form,
                    amount: form.amount,
                    tenure: form.tenure,
                    hasRenegotiation: data?.loanApplication.hasRenegotiation,
                    productBizId,
                    paymentInterval: form.paymentInterval ?? PaymentInterval.Monthly,
                }).toFixed(2))
            }

            setAcceptedProposalVariable({
                loanApplicationId: data?.loanApplication.id,
                insuranceAccepted: insuranceOptIn,
                proposal,
                simulation,
                form,
            });

            if (
                PRODUCTS_WITH_RECEIVABLES.includes(data.loanApplication.product.bizId) &&
                !data.loanApplication.hasPreviousApplication
            ) {
                goToLoanInformation();

            } else {
                setAcceptedProposalMutation({
                    variables: {
                        loanApplicationId: data?.loanApplication.id,
                        proposal,
                        insuranceAccepted: insuranceOptIn
                    }
                });

                redirect();
            }
        }

        setState({ ...state, submitting: false });
    }

    const redirect = async () => {
        try {
            setLivenessLoading(true);
            const response = await antiFraudQuery({
                variables: {
                    loanApplicationId: data?.loanApplication.id
                }
            })

            if (response.data.antiFraud) {
                if (APP_PROFILE !== 'partners') {
                    goToAntiFraudCheckout();
                } else {
                    goToShareAntiFraudUrl();
                }
            } else {
                nextScreen();
            }
        } catch {
            nextScreen();
        }
    }

    useEffect(() => {
        setCheckpoint('OfferMade');
    }, []);
    useEffect(initializeForm, [acceptedProposal]);
    useEffect(validateForSubmit, [state.submitting]);
    useEffect(submit, [invalidFields]);
    useEffect(() => {
        updateForm("amount", debouncedAmount);
    }, [debouncedAmount]);
    useEffect(() => {
        setDesktop(window.innerWidth > 767);
    }, [window.innerWidth]);
    useEffect(() => {
        const eligibleForInsurance = data?.loanApplication.isEligibleForInsurance && data?.loanApplication.product.bizId == CAPITAL_GIRO;

        setEligibleInsurance(eligibleForInsurance);
        setInsuranceEnabled(eligibleForInsurance);

        if (data?.loanApplication.product && data?.loanApplication.product.bizId) {
            setProductBizId(data?.loanApplication.product.bizId),
                setMinNumberOfInstallments(data?.loanApplication.product.minNumberOfInstallments),
                setMaxNumberOfInstallments(data?.loanApplication.product.maxNumberOfInstallments)
        }
    }, [data?.loanApplication]);
    useEffect(() => {
        setDiscountCalculated(
            calculateDiscounts(discounts, {
                ...data?.loanApplication,
                acceptedProposal: form
            })
        );
    }, [form, discounts]);

    useEffect(() => {
        if (loanApplicationUtmSource) {
            paymentDayOptions = PAYMENT_DAY.AVAILABLE_OPTIONS();
        } else {
            const utmSource = getUtmSource();
            if (utmSource) {
                setLoanApplicationUtmSource(utmSource);
            }
        }
    }, [loanApplicationUtmSource]);

    useEffect(() => {
        if (!loanApplicationUtmMedium) {
            const utmMedium = getUtmMedium();
            if (utmMedium) {
                setLoanApplicationUtmMedium(utmMedium);
            }
        }
    }, [loanApplicationUtmMedium]);


    const possibleInstallments = useMemo(() =>
        computePossibleInstallments(data?.loanApplication.lastProposal.tenure, data?.loanApplication.lastProposal.interestRate),
        [
            data?.loanApplication.lastProposal.tenure,
            data?.loanApplication.lastProposal.interestRate,
            computePossibleInstallments
        ]
    );

    const findError = (field: string) => invalidFields.find(invalidField => invalidField === field) && showValidation ? "ui-error" : "";

    const isValid = (variableName: string, value: any): boolean => {
        if (variableName === 'amount') {
            if (value > data?.loanApplication.lastProposal.amount || value < LOAN_AMOUNT.MIN()) {
                return false;
            }
        }
        return true;
    }

    const updateForm = (variableName: string, value: any, fieldName: string = variableName) => {
        if (value && isValid(variableName, value)) {
            _.remove(invalidFields, invalidField => invalidField === fieldName);
        } else {
            setInvalidFields(validateProposal({
                ...form,
                [variableName]: value
            }));
        }

        setForm({
            ...form,
            [variableName]: value
        });
    }

    const unsetCoupon = async () => {
        await unsetCouponCode({
            variables: {
                loanApplicationId: data?.loanApplication.id
            }
        })
    }

    const handleCouponInsertionClick = () => {
        setShowCouponInput(true);
        setCouponButtonClasses("d-none");
    }

    const handleCloseInputButton = () => {
        setShowCouponInput(false);
        setCouponError(false);
        setMinimumInterestError(false);
        setCouponCode("");
        setCouponButtonClasses("primary-text bg-white border-0 insert-coupon-button pl-0 coupon-hitbox");
    }

    const handleCloseCouponResponseButton = () => {
        unsetCoupon();
        setShowCouponResponseCard(false);
        setCouponError(false);
        setMinimumInterestError(false);
        setCouponCode("");
        setDiscounts([]);
    }

    const applyCoupon = async (value: string) => {
        setMinimumInterestError(false);
        const code = value && value.trim();
        const response = await availableCouponByCodeQuery({
            variables: {
                code
            }
        })

        if (response.data?.availableCouponByCode) {
            if (Number(data?.loanApplication.lastProposal.interestRate).toFixed(2) === "1.99"
                && hasInterestRateDiscount(response.data.availableCouponByCode.discounts, data?.loanApplication)) {
                setMinimumInterestError(true);
            } else {
                const { data: { applyForCoupon: canApply } } = await applyForCoupon({
                    variables: {
                        code,
                        loanApplicationId: data?.loanApplication.id
                    }
                });

                if (canApply) {
                    loadDiscounts(code, response.data.availableCouponByCode);
                }
            }
        } else {
            setCouponError(true);
        }
    }

    const loadDiscounts = async (code = data?.loanApplication.couponCode, coupon) => {
        if (code) {
            if (coupon && coupon.discounts && hasDiscountsToApply(coupon.discounts, data?.loanApplication)) {
                setDiscounts(coupon.discounts);
                setCouponCode(code);
                setShowCouponInput(true);
                setCouponButtonClasses("d-none");
                setShowCouponResponseCard(true);
                setShowMinimumInterestCard(true)
            }
        } else {
            const couponResult = getQueryParamsAsJson() as any;
            if (couponResult.coupon) {
                applyCoupon(couponResult.coupon);
            }
        }
    }

    if (loading || !form) {
        return (
            <Loading />
        );
    }

    const handleAmountRequested = (value: number): void => {
        setAmount(value);
        registerAnalyticsEvent('Campo', value);
        setLastInputValue(value);
    }

    const registerAnalyticsEvent = (eventAction: string, value: number) => {
        if (value >= LOAN_AMOUNT.MIN() && value <= data?.loanApplication.lastProposal.amount && lastInputValue !== value) {
            ReactGA.event({
                category: 'Interação Valor - Balcão',
                action: eventAction,
            });
        }
    }

    const handleCloseMinimumInterestResponseCard = () => {
        setShowMinimumInterestCard(false);
    }

    const containerClasses = ['loan-page'];

    if (showLoanConfigurationWithBorderedInput) {
        containerClasses.push('loan-page-bordered-input');
    }

    const getInterestRate = () => {
        if (data?.loanApplication.lastProposal.hasRejectedDiscount && !showCouponResponseCard) {
            const proposalWithDiscountIndex = data?.loanApplication
                .proposals
                .findIndex((proposal) => proposal.hasRejectedDiscount === true)

            return {
                previousRate: data?.loanApplication.proposals[proposalWithDiscountIndex - 1].interestRate,
                currentRate: data?.loanApplication.lastProposal.interestRate
            }
        }

        return {
            previousRate: data?.loanApplication.lastProposal.interestRate,
            currentRate: discountCalculated.calculatedInterestRate
        }
    }

    const handleInsuranceEnabledChanged = () => {
        setInsuranceEnabled(!insuranceEnabled);

        if (!insuranceEnabled) {
            setInsuranceOptIn(false);
        }
    }

    const handleInsuranceOptInChanged = () => {
        setInsuranceOptIn(!insuranceOptIn);
    }

    const handleOpenInsuranceAbout = () => {
        setShowInsuranceAbout(true);
    }

    const handleCloseInsuranceAbout = () => {
        setShowInsuranceAbout(false);
    }

    const getTitleText = () => {
        if (data && PRODUCTS_WITH_RECEIVABLES.includes(data.loanApplication.product.bizId)) {
            return (data.loanApplication.hasPreviousApplication)
                ? componentText.loanConfigurationTitleRejected
                : componentText.loanConfigurationTitleReceivables
        }

        return componentText.loanConfigurationTitle;
    }

    return (
        <>
            <div className={`${containerClasses.join(' ')} ${isDesktop && "pb-50"}`}>
                <span className="grid-company-document">
                    CNPJ {formatCnpj(data?.loanApplication.borrower.companyDocument)}
                </span>

                <Title>{isPreApproved ? 'simule o seu crédito pré-aprovado' : getTitleText()}</Title>
                <div className="grid-loan-value">
                    {data?.loanApplication.lastProposal.amount !== data?.loanApplication.amountRequested && !isAnyEstimuloProduct(loanApplicationUtmSource) && !isPreApproved ?
                        <Fragment>
                            <p className={`${data?.loanApplication.lastProposal.amount === LOAN_AMOUNT.MIN() ? 'mb-2-5rem' : ''}`}>
                                {showLoanConfigurationWithBorderedInput ?
                                    <>
                                        Seu pedido foi de R$ {formatMoney(data?.loanApplication.amountRequested)}, mas após a análise do
                                        histórico financeiro, conseguimos pré-aprovar até <span className="color-green font-weight-bold">R$ {formatMoney(data?.loanApplication.lastProposal.amount)}</span>
                                    </> :
                                    <>
                                        Nossa análise automática liberou um valor menor que R$ {formatMoney(data?.loanApplication.amountRequested)}.
                                        Configure as condições iniciais para seguir com a sua análise:
                                    </>
                                }
                            </p>
                        </Fragment> :
                        <p>
                            {showLoanConfigurationWithBorderedInput ?
                                <>
                                    O valor pré-aprovado é de <span className="color-green font-weight-bold">R$ {formatMoney(data?.loanApplication.lastProposal.amount)}</span>. Configure as condições iniciais para seguir com a sua análise:
                                </> :
                                <>
                                    Configure as condições iniciais para seguir com a sua análise:
                                </>
                            }
                        </p>
                    }

                    <div className="mb-1">
                        {!showLoanConfigurationWithBorderedInput && (
                            <>
                                <h2 className="loan-value">
                                    {formatMoneyWithoutDecimals(amount)}
                                </h2>
                                {!form.fixedComission && data?.loanApplication.lastProposal.amount !== LOAN_AMOUNT.MIN() &&
                                    <Slider
                                        value={amount}
                                        min={LOAN_AMOUNT.MIN()}
                                        max={data?.loanApplication.lastProposal.amount}
                                        className="loan-value-slider"
                                        onChange={({ value }) => setAmount(value)}
                                        onSelect={(value) => registerAnalyticsEvent('Slider', value)}
                                        id="amount_config"
                                    />
                                }
                            </>
                        )}

                        {showLoanConfigurationWithBorderedInput && (
                            <LoanAmountInput
                                value={amount}
                                prefix="R$ "
                                size='lg'
                                name='loanAmountInput'
                                onChange={handleAmountRequested}
                                lastProposal={data?.loanApplication.lastProposal.amount}
                            />
                        )}
                    </div>
                </div>

                <div className="grid-loan-config">
                    {!showLoanConfigurationWithBorderedInput &&
                        <>
                            <Dropdown
                                value={form.tenure}
                                id="term_config"
                                className={`grid-loan-config-installments extra-padding-bottom-mobile ${findError('tenure')}`}
                                options={possibleInstallments}
                                label="pagando em"
                                prime={true}
                                disabled={form.fixedComission}
                                onChange={(e) => updateForm("tenure", e.value)}
                            />

                            {discountCalculated.calculatedPaymentDay ?
                                (
                                    <Dropdown
                                        value={moment(discountCalculated.calculatedPaymentDay).format("YYYY-MM-DD")}
                                        id="payment_day_config"
                                        className={`grid-loan-config-payment-day ${findError('paymentDay')}`}
                                        options={[{
                                            label: moment(discountCalculated.calculatedPaymentDay).format('[Dia] DD'),
                                            value: moment(discountCalculated.calculatedPaymentDay).format("YYYY-MM-DD")
                                        }]}
                                        placeholder="Selecione um dia"
                                        label="vencimento mensal"
                                        prime={true}
                                        disabled={true}
                                        helperText={<>
                                            primeira parcela em
                                            <span className="outdated-value">
                                                {moment(form.paymentDay).format("DD/MM")}
                                            </span>
                                            <span className="current-value">
                                                {moment(discountCalculated.calculatedPaymentDay).format("DD/MM")}
                                            </span>
                                        </>}
                                    />
                                )
                                :
                                (
                                    <Dropdown
                                        value={form.paymentDay}
                                        id="payment_day_config"
                                        className={`grid-loan-config-payment-day ${findError('paymentDay')}`}
                                        options={paymentDayOptions}
                                        placeholder="Selecione um dia"
                                        label="vencimento mensal"
                                        onChange={(e) => updateForm("paymentDay", e.value)}
                                        prime={true}
                                        disabled={paymentDayOptions.length === 1}
                                        helperText={`primeira parcela em ${moment(form.paymentDay).format("DD/MM")}`}
                                    />
                                )
                            }
                        </>
                    }

                    {
                        showLoanConfigurationWithBorderedInput &&
                        <>
                            <Dropdown
                                value={form.tenure}
                                id="term_config"
                                className={`grid-loan-config-installments extra-padding-bottom-mobile ${findError('tenure')}`}
                                options={possibleInstallments}
                                label="pagando em"
                                prime={true}
                                disabled={form.fixedComission}
                                onChange={(e) => updateForm("tenure", e.value)}
                            />

                            {discountCalculated.calculatedPaymentDay ?
                                (
                                    <Dropdown
                                        value={moment(discountCalculated.calculatedPaymentDay).format("YYYY-MM-DD")}
                                        id="payment_day_config"
                                        className={`grid-loan-config-payment-day ${findError('paymentDay')}`}
                                        options={[{
                                            label: moment(discountCalculated.calculatedPaymentDay).format('[Dia] DD'),
                                            value: moment(discountCalculated.calculatedPaymentDay).format("YYYY-MM-DD")
                                        }]}
                                        placeholder="Selecione um dia" label="vencimento mensal"
                                        prime={true}
                                        disabled={true}
                                        helperText={<>
                                            primeira parcela em
                                            <span className="outdated-value">
                                                {moment(form.paymentDay).format("DD/MM")}
                                            </span>
                                            <span className="current-value">
                                                {moment(discountCalculated.calculatedPaymentDay).format("DD/MM")}
                                            </span>
                                        </>}
                                    />
                                )
                                :
                                (
                                    <Dropdown
                                        value={form.paymentDay}
                                        id="payment_day_config"
                                        className={`grid-loan-config-payment-day ${findError('paymentDay')}`}
                                        options={paymentDayOptions}
                                        placeholder="Selecione um dia"
                                        label="vencimento mensal"
                                        onChange={(e) => updateForm("paymentDay", e.value)}
                                        prime={true}
                                        helperText={`primeira parcela em ${moment(form.paymentDay).format("DD/MM")}`}
                                    />
                                )
                            }
                        </>
                    }

                    <div className="grid-loan-config-label">
                        <img src={interestIcon} className="pricing-icon"
                            srcSet={interestIcon2} />

                        <span className="interest-rate-label">
                            {discountCalculated.calculatedInterestRate || data?.loanApplication.lastProposal.hasRejectedDiscount ?
                                <>
                                    taxa de juros de <span className="outdated-value font-weight-bold">{Number(getInterestRate().previousRate).toFixed(2)}%</span>
                                    <span className="color-green font-weight-bold"> {Number(getInterestRate().currentRate).toFixed(2)}%</span> ao mês
                                </>
                                :
                                <>
                                    taxa de juros de <span className="color-green font-weight-bold">{Number(form.interestRate).toFixed(2)}%</span> ao mês
                                </>
                            }
                        </span>
                    </div>
                    {Number(discountCalculated.calculatedInterestRate).toFixed(2) === "1.99" && showMinimumInterestCard &&
                        <MinimumInterestResponseCard onCloseCardButton={handleCloseMinimumInterestResponseCard} />
                    }
                </div>

                {
                    eligibleInsurance &&
                    <div className="grid-loan-insurance-agree divider-border">
                        <Switch
                            label="Crédito mais seguro"
                            value={insuranceEnabled}
                            onChange={handleInsuranceEnabledChanged}
                        />
                    </div>
                }

                {
                    eligibleInsurance && insuranceEnabled &&
                    <div className="grid-loan-insurance">
                        <div className="grid-loan-insurance-section grid-loan-insurance-value">
                            <p className="insurance-text">
                                Valor do seguro
                            </p>
                            <h3 className="insurance-value-resume">
                                <div className="insurance-info primary-text">{form.tenure} x de</div>
                                <div className="insurance-info primary-text insurance-money">{formatMoney(simulation.insurancePmt)}</div>
                            </h3>
                            <p className="insurance-info insurance-resume">Total de</p>
                            <p className="insurance-info insurance-resume insurance-money">{formatMoney(simulation.insurancePremium)}</p>
                        </div>

                        <div className="grid-loan-insurance-section grid-loan-insurance-description">
                            <p className="insurance-text insurance-info">
                                Empréstimo pago em situações imprevistas como perda de renda, invalidez ou morte.
                            </p>
                        </div>

                        <div className="grid-loan-insurance-section grid-loan-insurance-about">
                            <a
                                onClick={handleOpenInsuranceAbout}
                                className="insurance-text insurance-about-link"
                            >
                                Saiba mais sobre o seguro
                            </a>
                            <Info />
                        </div>

                        <div className="grid-loan-insurance-section grid-loan-insurance-contract">
                            <Checkbox
                                value={insuranceOptIn}
                                onChange={handleInsuranceOptInChanged}
                                className="contract-checkbox"
                                renderLabel={() => (
                                    <div className="insurance-text">
                                        eu concordo com os
                                        <TermsDialog
                                            label="termos de contratação do seguro"
                                            title="TERMOS E CONDIÇÕES DO SEGURO"
                                            className="insurance-text insurance-contract-link"
                                        >
                                            <InsuranceTermsText />
                                        </TermsDialog>
                                        .
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                }

                {
                    CONFIG.COUPON.CAN_APPLY() &&
                    <div className="grid-loan-coupon divider-border">
                        <div>
                            <p className="d-inline-block">
                                tem um código promocional? <br className="d-block d-sm-none" />
                                <button className={couponButtonClasses} onClick={() => handleCouponInsertionClick()}>
                                    <span>
                                        insira agora
                                    </span>
                                    <img
                                        className="icon pl-2" src={dropdownIcon}
                                    />

                                </button>
                            </p>
                        </div>
                        {
                            showCouponInput &&
                            <CouponInput
                                applyCoupon={applyCoupon}
                                couponError={couponError}
                                minimumInterestError={minimumInterestError}
                                onChange={setCouponCode}
                                onCloseInputButton={handleCloseInputButton}
                                onCloseCouponCardButton={handleCloseCouponResponseButton}
                                placeholder="código"
                                showCouponResponseCard={showCouponResponseCard}
                                value={couponCode}
                                calculatedDiscounts={discountCalculated}
                                discounts={discounts}
                                loanApplication={data?.loanApplication}
                                id="cupom_use"
                            />
                        }
                    </div>
                }
                {paymentSummary}
            </div>

            {
                isDesktop &&
                <div className="footer-balcao">
                    <ButtonGroup className="button-group">
                        {![ESTIMULO_2020, ESTIMULO_2020_BR, ESTIMULO_2020_RS, SEBRAE].includes(loanApplicationUtmSource) && !isPreApproved &&
                            <Button
                                label="ver depois"
                                className="grid-button-backward"
                                variant="secondary"
                                onClick={goToFeedback}
                            />
                        }
                        {isPreApproved &&
                            <Button
                                label="voltar"
                                className="grid-button-backward"
                                variant="secondary"
                                onClick={goToPreApprovedHome}
                            />
                        }
                        <Button
                            label="continuar"
                            disabled={!formIsValid()}
                            className={`${invalidFields.length ? 'p-button-secondary' : ''} grid-button-forward ml-100`}
                            loading={livenessLoading}
                            onClick={() => onClickSubmit()}
                        />
                    </ButtonGroup>
                </div>
            }

            <div className="d-none d-sm-block mb-4" />

            {
                showInsuranceAbout &&
                <AboutInsurance onClose={handleCloseInsuranceAbout}></AboutInsurance>
            }
        </>
    )
}

export default LoanConfiguration;
